import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Avatar,
} from '@mui/material';
import { ChildCare } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { fullName } from '../../../lib/utils';

type Props = {
    mshipId: number;
};

type PupilData = {
    id: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    mship?: {
        id: number;
    };
};

type PupilChosen = {
    id: number | null;
    mship?: {
        id: number | null;
    };
};

export default function MshipPupils({ mshipId }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [pupils, setPupils] = useState<PupilData[]>([]);
    const [pupilChosen, setPupilChosen] = useState<PupilChosen>({
        id: null,
        mship: undefined,
    });

    useEffect(() => {
        if (mshipId) loadPupils();
    }, [mshipId]);

    const loadPupils = () => {
        cxt?.api('GET', `/mships/${mshipId}/pupils`, {
            params: { rows: 1000 },
            success: (r: any) => {
                setPupils(r.rows);
            },
        });
    };

    // Empty state
    if (!pupils || pupils.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    color: 'text.secondary',
                }}
            >
                <ChildCare sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1">
                    {t('generic.noPupilsAssigned')}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ p: 1 }}>
                <Grid container spacing={2}>
                    {pupils.map((pupil, idx) => (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={idx}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    cursor: pupil.mship?.id
                                        ? 'pointer'
                                        : 'not-allowed',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        boxShadow: pupil.mship?.id ? 2 : 0,
                                        borderColor: pupil.mship?.id
                                            ? 'primary.dark'
                                            : undefined,
                                    },
                                    opacity: pupil.mship?.id ? 1 : 0.7,
                                }}
                                onClick={() => {
                                    if (pupil.mship?.id) {
                                        setPupilChosen(pupil);
                                    } else {
                                        cxt.showMessage(
                                            'W',
                                            t(
                                                'generic.pupilNotRegisteredInCenter',
                                            ),
                                        );
                                    }
                                }}
                            >
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        p: 2,
                                        '&:last-child': { pb: 2 },
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            mb: 1,
                                            bgcolor: 'info.light',
                                        }}
                                    >
                                        <ChildCare
                                            sx={{
                                                fontSize: 20,
                                                color: 'info.main',
                                            }}
                                        />
                                    </Avatar>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {fullName(pupil as any)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <SttMshipDialog
                mshipId={pupilChosen.mship?.id ?? null}
                open={!!pupilChosen.mship?.id}
                onClose={() => {
                    setPupilChosen({
                        id: null,
                        mship: undefined,
                    });
                    loadPupils();
                }}
            />
        </>
    );
}
